/**
 * Logger utility for staging environment
 * Provides structured logging with different levels
 * In production, console logs will be removed/disabled
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

const CURRENT_LOG_LEVEL =
  process.env.NODE_ENV === "development" ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO;

class Logger {
  constructor(context = "App") {
    this.context = context;
  }

  // Log error messages
  error(message, error = null, metadata = {}) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.ERROR) {
      const logData = {
        level: "ERROR",
        context: this.context,
        message,
        timestamp: new Date().toISOString(),
        ...metadata,
      };

      if (error) {
        if (error instanceof Error) {
          logData.error = {
            name: error.name,
            message: error.message,
            stack: error.stack,
          };
        } else {
          logData.error = error;
        }
      }

      console.error(`[${this.context}] ERROR:`, message, logData);
    }
  }

  // Log warning messages
  warn(message, data = {}) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.WARN) {
      const logData = {
        level: "WARN",
        context: this.context,
        message,
        timestamp: new Date().toISOString(),
        ...data,
      };

      console.warn(`[${this.context}] WARN:`, message, logData);
    }
  }

  // Log info messages
  info(message, data = {}) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.INFO) {
      const logData = {
        level: "INFO",
        context: this.context,
        message,
        timestamp: new Date().toISOString(),
        ...data,
      };
    }
  }

  // Log debug messages
  debug(message, data = {}) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.DEBUG) {
      const logData = {
        level: "DEBUG",
        context: this.context,
        message,
        timestamp: new Date().toISOString(),
        ...data,
      };
    }
  }

  // Log Api request
  apiRequest(method, url, data = {}) {
    this.debug(`API Request: ${method} ${url}`, {
      method,
      url,
      requestData: data,
    });
  }

  // Log Api response
  apiResponse(method, url, response, status) {
    this.debug(`API Response: ${method} ${url}`, {
      method,
      url,
      status,
      responseData: response,
    });
  }

  // Log Api errors
  apiError(method, url, error, status = null) {
    this.error(`API Error: ${method} ${url}`, error, {
      method,
      url,
      status,
    });
  }

  // Log user action
  userAction(action, data = {}) {
    this.info(`User Action: ${action}`, {
      action,
      ...data,
    });
  }

  //Log performance metrics
  performance(operation, duration, metadata = {}) {
    this.info(`Performance: ${operation}`, {
      operation,
      duration,
      ...metadata,
    });
  }
}

export const authLogger = new Logger("Auth");
export const apiLogger = new Logger("API");
export const uiLogger = new Logger("UI");
export const errorLogger = new Logger("Error");
export const zustandLogger = new Logger("Zustand");

// Default logger
export const logger = new Logger("App");

export default Logger;
